import React, { useState } from 'react';
import {
  Box, Typography, Stack, Button, TextField, InputAdornment, Avatar, Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LinkIcon from '@mui/icons-material/Link';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { BRAND } from '../theme/tokens';
import { BTN_STANDARD, BTN_STATUS_GREEN } from '../theme/poButtonTokens';
import { StatusChip } from '../components/StatusChip';

const PAGE_MAX = 720;

/**
 * Full-page "Find Bill" flow from a PO — links an existing bill to this PO.
 * Replaces the modal dialog (Nielsen #6: recognition on a dedicated screen).
 */
export const POFindBillPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { purchaseOrders, bills, suppliers, updateBill } = useData();
  const [search, setSearch] = useState('');
  const [linkingId, setLinkingId] = useState<string | null>(null);

  const po = purchaseOrders.find(p => p.id === id);
  const supplier = po ? suppliers.find(s => s.id === po.supplierId) : null;

  if (!po || !supplier) {
    return (
      <Box sx={{ p: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/purchase-orders-list')}
          sx={{ textTransform: 'none', mb: 2 }}>
          Back to PO List
        </Button>
        <Typography color="error">Purchase order not found: {id}</Typography>
      </Box>
    );
  }

  const q = search.toLowerCase();
  const candidates = bills.filter(b => {
    if (b.linkedPoId === po.id) return false;
    if (!q) return true;
    const sup = suppliers.find(s => s.id === b.supplierId);
    return (
      b.billNo.toLowerCase().includes(q) ||
      (b.supplierInvoiceNo || '').toLowerCase().includes(q) ||
      (sup?.name || '').toLowerCase().includes(q) ||
      (b.jobRef || '').toLowerCase().includes(q)
    );
  });

  const handleLink = (billId: string) => {
    setLinkingId(billId);
    updateBill(billId, { linkedPoId: po.id });
    setTimeout(() => navigate(`/po-print/${po.id}`), 400);
  };

  return (
    <Box sx={{ maxWidth: PAGE_MAX, mx: 'auto', px: 2, py: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Button startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/po-print/${po.id}`)}
          sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#666',
            '&:hover': { backgroundColor: 'transparent', color: BRAND } }}>
          Back to {po.id}
        </Button>
      </Stack>

      <Paper elevation={0} sx={{ border: '1px solid #e0d5f5', borderRadius: 2, p: 3, backgroundColor: '#fdfcff' }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-start" mb={2}>
          <LinkIcon sx={{ color: BRAND, mt: 0.25 }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#1a1a2e' }}>
              Find bill for {po.id}
            </Typography>
            <Typography sx={{ fontSize: '0.76rem', color: '#666', mt: 0.35 }}>
              Search bills already in the system and link one to this purchase order.
              {supplier.name ? ` Supplier: ${supplier.name}.` : ''}
            </Typography>
          </Box>
        </Stack>

        <TextField
          fullWidth size="small" autoFocus
          placeholder="Search by bill #, supplier, invoice #, job…"
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ mb: 2.5 }}
        />

        {candidates.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center', border: '1px dashed #e0d5f5', borderRadius: 2, backgroundColor: '#faf8ff' }}>
            <Typography sx={{ fontSize: '0.82rem', color: '#b0a0d8', fontWeight: 600 }}>
              {search ? 'No bills match your search' : 'No other bills available to link'}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#ccc', mt: 0.5 }}>
              Try a different search, or create a new bill from the PO page
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.75}>
            {candidates.slice(0, 30).map(bill => {
              const sup = suppliers.find(s => s.id === bill.supplierId);
              const isLinking = linkingId === bill.id;
              const currentPo = bill.linkedPoId ? purchaseOrders.find(p => p.id === bill.linkedPoId) : null;
              return (
                <Box key={bill.id} sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25,
                  border: '1px solid #e8e2f4', borderRadius: 2, backgroundColor: '#fff',
                  '&:hover': { backgroundColor: '#f5f0ff', borderColor: `${BRAND}55` },
                }}>
                  <Avatar sx={{ width: 32, height: 32, backgroundColor: BRAND, fontSize: '0.62rem', fontWeight: 800 }}>
                    {sup?.code?.slice(0, 3) ?? '?'}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: BRAND }}>{bill.billNo}</Typography>
                      <StatusChip status={bill.status} dense />
                      {bill.processed && <StatusChip status="Imported" tone="success" dense />}
                    </Stack>
                    <Typography sx={{ fontSize: '0.7rem', color: '#555' }} noWrap>
                      {sup?.name ?? '—'}
                      {bill.supplierInvoiceNo ? ` · Inv #${bill.supplierInvoiceNo}` : ''}
                      {bill.jobRef ? ` · ${bill.jobRef}` : ''}
                    </Typography>
                    {currentPo && (
                      <Typography sx={{ fontSize: '0.62rem', color: '#e65100', fontStyle: 'italic', mt: 0.2 }}>
                        Currently linked to {currentPo.id} — will re-link to {po.id}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" spacing={0.5} flexShrink={0}>
                    <Button size="small" variant="outlined"
                      startIcon={<OpenInNewIcon sx={{ fontSize: 11 }} />}
                      onClick={() => navigate(`/bills/${bill.id}`)}
                      sx={{ textTransform: 'none', fontSize: '0.65rem', color: '#555', borderColor: '#ccc', py: 0.3, px: 0.8, minWidth: 0 }}>
                      View
                    </Button>
                    <Button size="small" variant="contained" disableElevation
                      startIcon={isLinking ? undefined : <LinkIcon sx={{ fontSize: 11 }} />}
                      disabled={isLinking}
                      onClick={() => handleLink(bill.id)}
                      sx={{ ...BTN_STANDARD, fontSize: '0.65rem', height: 28, px: 1, minWidth: 0 }}>
                      {isLinking ? '✓ Linked' : 'Link to PO'}
                    </Button>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end" mt={3} pt={2} borderTop="1px solid #eee">
          <Button onClick={() => navigate(`/po-print/${po.id}`)}
            sx={{ textTransform: 'none', color: '#666' }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation
            startIcon={<ReceiptLongIcon sx={{ fontSize: 15 }} />}
            onClick={() => navigate(`/po-print/${po.id}`)}
            sx={BTN_STATUS_GREEN}>
            Back to PO — create new bill
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default POFindBillPage;
